import type { Relationships, RestrictionView } from '@homecare/contracts';
import { EXCLUDED_REASON_MIN_LENGTH } from '@homecare/contracts';
import { Button, Card, EmptyState, Field, SelectField, Spinner, StatusPill } from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

/**
 * Restrictions and orientation, on both records (slice-02 session 7, exit
 * criterion 6).
 *
 * The person who needs "do not send this aide, and why" is as often looking at
 * the client as at the caregiver, so the same pairings appear on both. Each
 * side shows the reason in full. Each side names the *other* party only to a
 * caller who may read that kind of record — HR sees that an exclusion exists
 * and why, not which client; a care manager sees which aide is excluded only if
 * they may read caregivers. The API redacts; this renders what it is given.
 */

const TYPE_TONE = { EXCLUDED: 'critical', PREFERRED: 'verified' } as const;
const TYPE_LABEL = { EXCLUDED: 'Do not send', PREFERRED: 'Preferred' } as const;

const when = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));

function RestrictionList({
  restrictions,
  other,
  onRemove,
}: {
  restrictions: RestrictionView[];
  other: 'client' | 'caregiver';
  onRemove?: (restriction: RestrictionView) => void;
}): ReactNode {
  return (
    <ul className="mt-2 grid gap-2 text-sm">
      {restrictions.map((restriction) => {
        const name = other === 'client' ? restriction.clientName : restriction.caregiverName;
        return (
          <li key={restriction.id} className="border-line grid gap-1 rounded-md border p-3">
            <span className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-ink font-medium">
                {name ??
                  (other === 'client'
                    ? 'A client you don’t have access to'
                    : 'A caregiver you don’t have access to')}
              </span>
              <StatusPill tone={TYPE_TONE[restriction.type]}>
                {TYPE_LABEL[restriction.type]}
              </StatusPill>
            </span>
            {restriction.reason && (
              <span className="text-ink-2">
                <span className="font-medium">Why: </span>
                {restriction.reason}
              </span>
            )}
            <span className="text-ink-3 text-xs">
              Recorded {when(restriction.createdAt)}
              {restriction.createdByName ? ` by ${restriction.createdByName}` : ''}
            </span>
            {onRemove && (
              <span>
                <Button variant="ghost" size="sm" onClick={() => onRemove(restriction)}>
                  Remove…
                </Button>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Orientations({
  relationships,
  other,
}: {
  relationships: Relationships;
  other: 'client' | 'caregiver';
}): ReactNode {
  if (relationships.orientations.length === 0) {
    return <p className="text-ink-3 mt-1 text-sm">No orientation visits recorded.</p>;
  }
  return (
    <ul className="mt-2 grid gap-1 text-sm">
      {relationships.orientations.map((orientation) => {
        const name = other === 'client' ? orientation.clientName : orientation.caregiverName;
        return (
          <li key={orientation.id} className="text-ink">
            {name ?? (other === 'client' ? 'A client' : 'A caregiver')} · oriented{' '}
            {when(orientation.completedAt)}
            {orientation.completedByName ? ` · recorded by ${orientation.completedByName}` : ''}
            {orientation.note ? <span className="text-ink-3"> — {orientation.note}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** On the caregiver record: add, remove, and record orientation. */
export function CaregiverRelationshipsCard({ caregiverId }: { caregiverId: string }): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const canRead = useHasPermission('caregiver:read');
  const canEdit = useHasPermission('caregiver:update');
  const canPickClients = useHasPermission('client:read');

  const [mode, setMode] = useState<'restriction' | 'orientation' | null>(null);
  const [removing, setRemoving] = useState<RestrictionView | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const key = ['caregivers', 'relationships', caregiverId];
  const relationships = useQuery({
    queryKey: key,
    queryFn: () => api.get<Relationships>(`/v1/caregivers/${caregiverId}/client-relationships`),
    enabled: canRead,
    staleTime: 0,
  });

  if (!canRead) return null;

  const write = async (work: () => Promise<Relationships>): Promise<boolean> => {
    setBusy(true);
    setFailure(null);
    try {
      queryClient.setQueryData(key, await work());
      return true;
    } catch (error) {
      setFailure(error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const remove = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!removing) return;
    const ok = await write(() =>
      api.send<Relationships>(
        'POST',
        `/v1/caregivers/${caregiverId}/restrictions/${removing.id}/remove`,
        { reason: removeReason.trim() },
      ),
    );
    if (ok) {
      setRemoving(null);
      setRemoveReason('');
    }
  };

  const data = relationships.data;
  const addDisabled = !canPickClients
    ? {
        disabled: true as const,
        disabledReason:
          'Choosing a client needs access to client records, which your role does not have. Ask someone who can see clients to add it.',
      }
    : {};

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Clients: restrictions and orientation</h2>
          <p className="text-ink-3 mt-1 text-sm">
            Who this caregiver must not be sent to, and why — shown on the client’s record too.
          </p>
        </div>
        {canEdit && mode === null && (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setMode('restriction')}
              {...addDisabled}
            >
              Add a restriction
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setMode('orientation')}
              {...addDisabled}
            >
              Record an orientation
            </Button>
          </div>
        )}
      </div>

      <ErrorNote error={failure ?? relationships.error} nextStep="Check the entry and try again." />

      {mode !== null && (
        <PairForm
          mode={mode}
          busy={busy}
          onCancel={() => setMode(null)}
          onSubmit={async (body) => {
            const ok = await write(() =>
              mode === 'restriction'
                ? api.send<Relationships>(
                    'POST',
                    `/v1/caregivers/${caregiverId}/restrictions`,
                    body,
                  )
                : api.send<Relationships>(
                    'POST',
                    `/v1/caregivers/${caregiverId}/orientations`,
                    body,
                  ),
            );
            if (ok) setMode(null);
          }}
        />
      )}

      {removing && (
        <form
          onSubmit={(event) => void remove(event)}
          className="border-line mt-4 grid gap-3 rounded-md border p-4"
        >
          <p className="text-ink text-sm">
            Removing this lets the scheduler send this caregiver again. The restriction and your
            reason are kept on record.
          </p>
          <Field
            label="Why it no longer applies"
            required
            value={removeReason}
            onChange={(event) => setRemoveReason(event.target.value)}
          />
          <div className="flex gap-2">
            <Button type="submit" variant="danger" pending={busy}>
              Remove the restriction
            </Button>
            <Button type="button" variant="ghost" onClick={() => setRemoving(null)}>
              Keep it
            </Button>
          </div>
        </form>
      )}

      {relationships.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 py-4 text-sm" role="status">
          <Spinner /> Loading…
        </p>
      ) : data ? (
        <div className="mt-4 grid gap-6">
          <section>
            <h3 className="text-ink text-sm font-bold">Restrictions</h3>
            {data.restrictions.length === 0 ? (
              <EmptyState
                title="No restrictions"
                explanation="If a client or their family must not have this caregiver, record it here with the reason — the scheduler refuses the pairing, and the reason stays with it."
              />
            ) : (
              <RestrictionList
                restrictions={data.restrictions}
                other="client"
                {...(canEdit ? { onRemove: setRemoving } : {})}
              />
            )}
          </section>
          <section>
            <h3 className="text-ink text-sm font-bold">Orientation</h3>
            <Orientations relationships={data} other="client" />
          </section>
        </div>
      ) : null}
    </Card>
  );
}

function PairForm({
  mode,
  busy,
  onCancel,
  onSubmit,
}: {
  mode: 'restriction' | 'orientation';
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
}): ReactNode {
  const api = useApi();
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState('');
  const [type, setType] = useState<'EXCLUDED' | 'PREFERRED'>('EXCLUDED');
  const [reason, setReason] = useState('');
  const [completedOn, setCompletedOn] = useState('');
  const [note, setNote] = useState('');

  const clients = useQuery({
    queryKey: ['clients', 'picker', search],
    queryFn: () =>
      api.get<{ clients: { id: string; firstName: string; lastName: string; mrn: string }[] }>(
        `/v1/clients?search=${encodeURIComponent(search)}&pageSize=20`,
      ),
  });

  const tooShort =
    mode === 'restriction' &&
    type === 'EXCLUDED' &&
    reason.trim().length < EXCLUDED_REASON_MIN_LENGTH;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void onSubmit(
      mode === 'restriction'
        ? { clientId, type, reason: reason.trim() }
        : { clientId, ...(completedOn ? { completedOn } : {}), note: note.trim() || null },
    );
  };

  return (
    <form
      onSubmit={submit}
      className="border-line mt-4 grid gap-3 rounded-md border p-4 sm:grid-cols-2"
    >
      <Field
        label="Find a client"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <SelectField
        label="Client"
        required
        value={clientId}
        onChange={(event) => setClientId(event.target.value)}
      >
        <option value="">Choose…</option>
        {(clients.data?.clients ?? []).map((client) => (
          <option key={client.id} value={client.id}>
            {client.firstName} {client.lastName} · {client.mrn}
          </option>
        ))}
      </SelectField>

      {mode === 'restriction' ? (
        <>
          <SelectField
            label="Restriction"
            value={type}
            onChange={(event) => setType(event.target.value as 'EXCLUDED' | 'PREFERRED')}
          >
            <option value="EXCLUDED">Do not send this caregiver</option>
            <option value="PREFERRED">Preferred for this client</option>
          </SelectField>
          <Field
            label={type === 'EXCLUDED' ? 'Why (required)' : 'Why (optional)'}
            hint={
              type === 'EXCLUDED'
                ? `At least ${EXCLUDED_REASON_MIN_LENGTH} characters. Write it for whoever reads this in six months: an exclusion with no reason looks like a mistake, and gets removed.`
                : 'What makes this a good match, if it helps the scheduler.'
            }
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </>
      ) : (
        <>
          <Field
            label="Oriented on"
            hint="Leave blank for today."
            type="date"
            value={completedOn}
            onChange={(event) => setCompletedOn(event.target.value)}
          />
          <Field
            label="Note (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </>
      )}

      <div className="flex gap-2 sm:col-span-2">
        <Button
          type="submit"
          pending={busy}
          {...(!clientId
            ? { disabled: true as const, disabledReason: 'Choose the client first.' }
            : tooShort
              ? {
                  disabled: true as const,
                  disabledReason: `Say why in at least ${EXCLUDED_REASON_MIN_LENGTH} characters.`,
                }
              : {})}
        >
          {mode === 'restriction' ? 'Save the restriction' : 'Save the orientation'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** On the client record: read-only — the pairing is managed from the caregiver. */
export function ClientRelationshipsCard({ clientId }: { clientId: string }): ReactNode {
  const api = useApi();
  const canRead = useHasPermission('client:read');

  const relationships = useQuery({
    queryKey: ['clients', 'relationships', clientId],
    queryFn: () => api.get<Relationships>(`/v1/clients/${clientId}/caregiver-relationships`),
    enabled: canRead,
    staleTime: 0,
  });

  if (!canRead) return null;
  const data = relationships.data;

  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">Caregivers: restrictions and orientation</h2>
      <p className="text-ink-3 mt-1 text-sm">
        Who must not be sent to this client, and why. Managed from each caregiver’s record.
      </p>

      <ErrorNote error={relationships.error} nextStep="Reload the page and try again." />

      {relationships.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 py-4 text-sm" role="status">
          <Spinner /> Loading…
        </p>
      ) : data ? (
        <div className="mt-4 grid gap-6">
          <section>
            <h3 className="text-ink text-sm font-bold">Restrictions</h3>
            {data.restrictions.length === 0 ? (
              <p className="text-ink-3 mt-1 text-sm">No caregiver is restricted for this client.</p>
            ) : (
              <RestrictionList restrictions={data.restrictions} other="caregiver" />
            )}
          </section>
          <section>
            <h3 className="text-ink text-sm font-bold">Orientation</h3>
            <Orientations relationships={data} other="caregiver" />
          </section>
        </div>
      ) : null}
    </Card>
  );
}
